import * as React from 'react';
import Timeline from 'antd/lib/timeline';
import { courierTrackingStyles } from './consignment-details.styles';
import { CheckCircleOutlined, EnvironmentTwoTone } from '@ant-design/icons';
import { HocOptions } from '../common/generic-hoc.types';
import { TrackingEvent } from '../../library/Types';
import { StylesProps } from '../../theme/jss-types';
import Helper from '../../library/Helper';
import GenericHoc from '../common/generic-hoc';
import { fetchConsignmentTracking } from '../../network/consignments.api';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import GalleryModal from '../common/gallery-modal';
import { TrackTypeEnum } from 'types/view.types';
import { Spin } from 'antd';
import FallbackPage from 'components/common/fallback';
import { ReduxStore } from 'reducers/redux.types';
import StatusPrettyName from './status-pretty-name';

interface IProps extends StylesProps<ReturnType<typeof courierTrackingStyles>> {
    referenceNumber: string;
    dontShowTitle?: boolean;
    toggle?: boolean;
    trackType?: string;
    renderEddDates?: () => JSX.Element;
    tracking?: any;
    maxTimeline?: number;
    isMobile?: boolean;
    renderStatus?: () => JSX.Element;
    customerIdList?: string | number;
    useEventPrettyNameOverridesInTracking?: boolean;
}

interface GalleryModalData {
    images: string[];
    videos: string[];
}

const ConsignmentTracking = (props: IProps) => {
    const {
        classes,
        referenceNumber,
        dontShowTitle,
        toggle,
        trackType,
        renderEddDates,
        tracking: propsTracking,
        isMobile,
        maxTimeline,
        renderStatus,
        customerIdList,
        useEventPrettyNameOverridesInTracking,
    } = props;
    const { t, i18n } = useTranslation();
    const [tracking, setTracking] = React.useState<any>(propsTracking || {});
    const [loading, setLoading] = React.useState<boolean>(!propsTracking);
    const [isGalleryModalVisible, setIsGalleryModalVisible] = React.useState(false);
    const [galleryModalData, setGalleryModalData] = React.useState<GalleryModalData>({ images: [], videos: [] });
    const [showError, setShowError] = React.useState(false);

    const loadTracking = async () => {
        setLoading(true);
        let trackingResponse;
        const searchNumber = referenceNumber;
        switch (trackType) {
            case TrackTypeEnum.CUSTOMER_REFERENCE_NO:
                trackingResponse = await fetchConsignmentTracking(
                    { customerReferenceNumber: searchNumber, userId: customerIdList },
                );
                break;
            case TrackTypeEnum.CARRIER_AWB_NUMBER:
                trackingResponse = await fetchConsignmentTracking(
                    { courierPartnerReferenceNumber: searchNumber, userId: customerIdList },
                );
                break;
            default:
                trackingResponse = await fetchConsignmentTracking(
                    { referenceNumber: searchNumber, userId: customerIdList },
                );
        }

        if (trackingResponse.isSuccess) {
            const trackingData = (trackingResponse?.data?.events || []);
            setShowError(false);
            setTracking(trackingData || []);
        } else {
            setShowError(true);
        }
        setLoading(false);
    };

    React.useEffect(() => {
        if (!propsTracking) {
            loadTracking();
        }
    }, [toggle]);

    const dot = (event: any) => {
        return (
            <CheckCircleOutlined
                className={classes.dot}
                style={{
                    color: event.type === 'DELIVERED' ? '#27B479' : '#006EC3',
                }}
            />
        );
    };

    const getDate = (input: string) => {
        if (!input) {
            return null;
        }
        return moment(input).format('DD MMM YYYY');
    };

    const getTime = (input: string) => {
        if (!input) {
            return null;
        }
        return moment(input).format('hh:mm A');
    };

    const dateTimeline = (event: TrackingEvent) => {
        return (
            <div className={classes.dateTimeline} style={{ width: '25%' }}>
                <div className={classes.date}>
                    {getDate(event.event_time || event.strActionDate)}
                </div>
                <div className={classes.time}>
                    {getTime(event.event_time || event.strActionTime)}
                </div>
            </div>
        );
    };

    const getEventName = (event: TrackingEvent) => {
        if (event.strAction) {
            return event.strAction;
        }
        if (event.event_name) {
            return event.event_name;
        }
        if (i18n.exists(event.type)) {
            return t(event.type);
        }
        return Helper.snakeToPretty(event.type);
    };

    const eventTimeline = (event: TrackingEvent) => {
        return (
            <div className={classes.eventTimeline}>
                <div className={classes.textRight}>
                    {useEventPrettyNameOverridesInTracking
                        ? <StatusPrettyName status={event.type} />
                        : getEventName(event)}
                </div>
                <div
                    className={classes.eventLocation}
                    style={{
                        alignSelf: 'flex-start',
                    }}
                >
                    {event.tracking_note}
                </div>
                <div
                    className={classes.eventLocation}
                    style={{
                        alignSelf: 'flex-start',
                    }}
                >
                    {event.tracking_location_string || event.strOrigin || event.hub_name}
                </div>
                <div>
                    {
                        event.type === 'attempted'
                            ? (
                                <div>{event.failure_reason}</div>
                            )
                            : (<div />)
                    }

                    {
                        event.type === 'delivered' && (event.poc_image || event.premise_image || event.event_proof_images)
                            ? (
                                <div>
                                    <div>
                                        <a onClick={() => {
                                            setIsGalleryModalVisible(true);
                                            setGalleryModalData({
                                                images: event.event_proof_images || [event.poc_image || event.premise_image],
                                                videos: [],
                                            });
                                        }}
                                        >
                                            {t('tracking_view_pod')}
                                        </a>
                                    </div>
                                </div>
                            )
                            : (<div />)
                    }
                    {
                        event.type === 'delivered' && event.signature_image
                            ? (
                                <div>
                                    <a onClick={() => {
                                        setIsGalleryModalVisible(true);
                                        setGalleryModalData({
                                            images: [event.signature_image],
                                            videos: [],
                                        });
                                    }}
                                    >
                                        {t('tracking_view_customer_signature')}
                                    </a>
                                </div>
                            )
                            : (<div />)
                    }
                    {
                        ['delivered'].includes(event.type)
                            ? (
                                <>
                                    <div
                                        className={classes.eventLocation}
                                        style={{
                                            alignSelf: 'flex-start',
                                        }}
                                    >
                                        OTP Verified:
                                        {event.otp_success ? ' Yes' : ' No' }
                                    </div>
                                </>
                            )
                            : (<div />)
                    }
                </div>
            </div>
        );
    };

    const timelineItem = (event: TrackingEvent, idx: number) => {
        const trackingSize = tracking?.length || 0;
        return (
            <div className={classes.flexRow}>
                <Timeline.Item
                    className={(idx === trackingSize - 1) ? classes.lineItemNoTail : classes.timelineItem}
                    dot={dot(event)}
                />
                <div
                    style={{ width: '100%', display: 'flex', height: 'fit-content' }}
                    className={isMobile ? classes.box : ''}
                >
                    {eventTimeline(event)}
                    {dateTimeline(event)}
                </div>
            </div>
        );
    };

    const getTrackingItems = () => {
        const newTrackingItems: TrackingEvent[] = [];
        tracking.forEach((event: TrackingEvent) => {
            newTrackingItems.push(event);
        });
        if (typeof maxTimeline === 'number' && maxTimeline > 0 && maxTimeline < newTrackingItems.length) {
            return newTrackingItems.slice(0, maxTimeline);
        }
        return newTrackingItems;
    };

    const timeline = () => {
        const trackingToShow = getTrackingItems();
        return (
            <Timeline className={classes.timeline}>
                {trackingToShow?.map((item: TrackingEvent, idx: number) => (timelineItem(item, idx)))}
            </Timeline>
        );
    };

    const renderTracking = () => {
        return (
            <div
                className={classes.trackingBox}
                style={{
                    justifyItems: isMobile ? 'stretch' : 'center',
                    backgroundColor: isMobile ? '#f9fafb' : '#fff',
                    marginBottom: isMobile ? 0 : 50,
                }}
            >
                {timeline()}
            </div>
        );
    };

    const renderTitle = () => {
        if (dontShowTitle) {
            return null;
        }
        return (
            <div className={classes.trackingHeader}>
                <div className={classes.trackingTitle}>
                    {'   '}
                    <EnvironmentTwoTone twoToneColor="#52c41a" />
                    {'   '}
                    {t('tracking_timeline')}
                </div>
                <div>
                    {renderEddDates && renderEddDates()}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <Spin
                style={{
                    marginLeft: '48%',
                    marginTop: '40%',
                }}
            />
        );
    }

    if (showError) {
        return (
            <FallbackPage
                errorMsg={t('consignment_not_found')}
            />
        );
    }

    return (
        <div
            className={classes.mainDiv}
            style={{
                backgroundColor: isMobile ? '#f9fafb' : '#fff',
                height: maxTimeline ? 'fit-content' : '100%',
            }}
        >
            {renderTitle()}
            {!dontShowTitle && <div className={classes.hr} />}
            {renderStatus && renderStatus()}
            {renderTracking()}
            {isGalleryModalVisible && (
                <GalleryModal
                    isVisible={isGalleryModalVisible}
                    imageVideoGalleryData={galleryModalData}
                    handleModalClose={() => { setIsGalleryModalVisible(false); }}
                />
            )}
        </div>
    );
};

const mapStateToProps = (state: ReduxStore) => ({
    customerIdList: state.generic.CONSIGNMENTS?.appliedFilters?.customerIdList,
    useEventPrettyNameOverridesInTracking:
        state.master?.config?.use_event_pretty_name_overrides_in_tracking,
});

const options: HocOptions = {
    connectJss: {
        useJss: true,
        styleSheet: courierTrackingStyles,
    },
    connectRedux: {
        useRedux: true,
        mapStateToProps,
    },
};

const Courier = GenericHoc(options)(ConsignmentTracking);
export default Courier;
